import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { supabasePublic } from '@/integrations/supabase/publicClient';
import { mapZoneWindowRow, type ZoneWindow } from '@/lib/zoneScheduleUtils';

interface UseZoneTimeWindowsOptions {
  establishmentId?: string | null;
  /** Use authenticated client (staff side) vs anon public client (/book) */
  useAuthenticatedData?: boolean;
}

export function zoneTimeWindowsQueryKey(establishmentId?: string | null) {
  return ['zone-time-windows', establishmentId ?? null] as const;
}

/**
 * Horaires propres aux salles (zone_time_windows), plages actives uniquement.
 * Lecture publique alignée sur la RLS (établissements actifs).
 */
export const useZoneTimeWindows = (options: UseZoneTimeWindowsOptions = {}) => {
  const { establishmentId, useAuthenticatedData = true } = options;

  const query = useQuery({
    queryKey: zoneTimeWindowsQueryKey(establishmentId),
    queryFn: async (): Promise<ZoneWindow[]> => {
      if (!establishmentId) return [];
      const client = useAuthenticatedData ? supabase : supabasePublic;
      const { data, error } = await client
        .from('zone_time_windows')
        .select('zone_id, start_time, end_time, available_days, is_active')
        .eq('establishment_id', establishmentId)
        .eq('is_active', true);
      if (error) throw error;
      return (data || []).map(mapZoneWindowRow);
    },
    enabled: Boolean(establishmentId),
    staleTime: 1000 * 60 * 2,
  });

  return {
    zoneWindows: query.data || [],
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

export interface ZoneWindowRow extends ZoneWindow {
  id: string;
}

export interface ZoneWindowInput {
  zoneId: string;
  startTime: string; // "HH:MM"
  endTime: string;   // "HH:MM"
  availableDays: number[]; // 0 = dimanche ; vide interdit par la base
  isActive: boolean;
}

/**
 * Gestion admin des horaires propres aux salles : lit TOUTES les plages
 * (actives et inactives) et expose création / modification / suppression.
 * Réservé à la page Établissement (admin uniquement) ; la RLS impose le rôle admin.
 */
export const useZoneTimeWindowsAdmin = (establishmentId?: string | null) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [...zoneTimeWindowsQueryKey(establishmentId), 'admin'] as const,
    queryFn: async (): Promise<ZoneWindowRow[]> => {
      if (!establishmentId) return [];
      const { data, error } = await supabase
        .from('zone_time_windows')
        .select('id, zone_id, start_time, end_time, available_days, is_active')
        .eq('establishment_id', establishmentId)
        .order('start_time');
      if (error) throw error;
      return (data || []).map((row) => ({ id: row.id, ...mapZoneWindowRow(row) }));
    },
    enabled: Boolean(establishmentId),
    staleTime: 1000 * 30,
  });

  const invalidate = () => {
    // Invalide la vue admin ET la vue publique (même racine de clé)
    queryClient.invalidateQueries({ queryKey: zoneTimeWindowsQueryKey(establishmentId) });
  };

  const createWindow = useMutation({
    mutationFn: async (input: ZoneWindowInput) => {
      if (!establishmentId) throw new Error('Établissement manquant');
      const { error } = await supabase.from('zone_time_windows').insert({
        establishment_id: establishmentId,
        zone_id: input.zoneId,
        start_time: input.startTime,
        end_time: input.endTime,
        available_days: input.availableDays,
        is_active: input.isActive,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const updateWindow = useMutation({
    mutationFn: async ({ id, ...input }: ZoneWindowInput & { id: string }) => {
      const { error } = await supabase
        .from('zone_time_windows')
        .update({
          start_time: input.startTime,
          end_time: input.endTime,
          available_days: input.availableDays,
          is_active: input.isActive,
        })
        .eq('id', id)
        .eq('establishment_id', establishmentId ?? '');
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const deleteWindow = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('zone_time_windows')
        .delete()
        .eq('id', id)
        .eq('establishment_id', establishmentId ?? '');
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return {
    windows: query.data || [],
    loading: query.isLoading,
    error: query.error,
    createWindow,
    updateWindow,
    deleteWindow,
  };
};
